/**
 * Centralized category definitions per product type.
 * Single source of truth — used by admin forms and the storefront.
 */

export const CANVAS_CATEGORIES = [
  { slug: "nature-landscape",  label: "Nature & Landscape" },
  { slug: "city-architecture", label: "City & Architecture" },
  { slug: "animals-wildlife",  label: "Animals & Wildlife" },
  { slug: "abstract-modern",   label: "Abstract & Modern" },
  { slug: "travel-places",     label: "Travel & Places" },
  { slug: "black-white",       label: "Black & White" },
  { slug: "luxury-statement",  label: "Luxury & Statement" },
  { slug: "kids-playful",      label: "Kids & Playful" },
];

export const MURAL_CATEGORIES = [
  { slug: "nature-landscape",          label: "Nature & Landscape" },
  { slug: "abstract-texture",          label: "Abstract & Texture" },
  { slug: "3d-illusion",               label: "3D Illusion" },
  { slug: "kids-nursery",              label: "Kids & Nursery" },
  { slug: "urban-architecture",        label: "Urban & Architecture" },
  { slug: "luxury-interiors",          label: "Luxury Interiors" },
  { slug: "office-commercial",         label: "Office & Commercial" },
  { slug: "brick-concrete-industrial", label: "Brick, Concrete & Industrial" },
];

export const STICKER_CATEGORIES = [
  { slug: "product-labels",  label: "Product Labels" },
  { slug: "custom-stickers", label: "Custom Stickers" },
  { slug: "packaging",       label: "Packaging" },
  { slug: "promotional",     label: "Promotional" },
  { slug: "safety-warning",  label: "Safety & Warning" },
  { slug: "window-decals",   label: "Window Decals" },
];

export const BANNER_CATEGORIES = [
  { slug: "roller-banners",   label: "Roller Banners" },
  { slug: "pvc-banners",      label: "PVC Banners" },
  { slug: "mesh-banners",     label: "Mesh Banners" },
  { slug: "fabric-banners",   label: "Fabric Banners" },
  { slug: "outdoor-signage",  label: "Outdoor Signage" },
  { slug: "event-displays",   label: "Event Displays" },
];

export const VEHICLE_CATEGORIES = [
  { slug: "full-wraps",        label: "Full Wraps" },
  { slug: "partial-wraps",     label: "Partial Wraps" },
  { slug: "van-graphics",      label: "Van Graphics" },
  { slug: "fleet-branding",    label: "Fleet Branding" },
  { slug: "magnetic-signs",    label: "Magnetic Signs" },
  { slug: "window-graphics",   label: "Window Graphics" },
];

/**
 * Returns the category list for a given product type string.
 * Matches loosely (case-insensitive, substring).
 * When productType is empty/unknown, returns all categories so the
 * combobox always shows selectable options.
 */
export function getCategoriesForType(productType) {
  const pt = (productType || "").toLowerCase();
  if (pt.includes("canvas"))   return CANVAS_CATEGORIES;
  if (pt.includes("mural"))    return MURAL_CATEGORIES;
  if (pt.includes("sticker") || pt.includes("label")) return STICKER_CATEGORIES;
  if (pt.includes("banner"))   return BANNER_CATEGORIES;
  if (pt.includes("vehicle") || pt.includes("wrap"))  return VEHICLE_CATEGORIES;
  // No match — return combined list so the combobox is never empty
  return ALL_CATEGORIES;
}

/** De-duplicated union of every category list, plus product-type entries for discoverability. */
export const ALL_CATEGORIES = (() => {
  const seen = new Set();
  const result = [];
  // Add product-type level entries so they're always findable
  const typeEntries = [
    { slug: "wall-murals",       label: "Wall Murals" },
    { slug: "canvas-prints",     label: "Canvas Prints" },
    { slug: "stickers-labels",   label: "Stickers & Labels" },
    { slug: "banners",           label: "Banners" },
    { slug: "vehicle-graphics",  label: "Vehicle Graphics" },
    { slug: "floor-graphics",    label: "Floor Graphics" },
  ];
  for (const c of typeEntries) {
    seen.add(c.slug);
    result.push(c);
  }
  for (const list of [CANVAS_CATEGORIES, MURAL_CATEGORIES, STICKER_CATEGORIES, BANNER_CATEGORIES, VEHICLE_CATEGORIES]) {
    for (const c of list) {
      if (!seen.has(c.slug)) {
        seen.add(c.slug);
        result.push(c);
      }
    }
  }
  return result;
})();
